import {
  InterviewAttempt,
  InterviewData,
  InterviewRank,
  StudentAttempt,
} from "@/types";

/**
 * Get module name from module number
 */
export const getModuleName = (moduleNumber: number): string => {
  const modules = ["HTML & CSS", "JavaScript", "Frontend (React)", "Backend"];
  return modules[moduleNumber] || "Unknown";
};

/**
 * Get status badge variant for interview status
 */
export const getStatusBadgeVariant = (
  status: string
): "default" | "secondary" | "destructive" | "outline" | "dangerous" => {
  switch (status) {
    case "completed":
      return "default";
    case "scheduled":
      return "secondary";
    case "missed":
      return "destructive";
    case "cancelled":
      return "dangerous";
    default:
      return "outline";
  }
};

/**
 * Calculate final rank from individual ranks
 */
export const calculateFinalRank = (ranks: InterviewRank): number => {
  const { html_css, javascript, react, backend } = ranks;
  const total = html_css + javascript + react + backend;
  const count = [html_css, javascript, react, backend].filter(
    (r) => r > 0
  ).length;
  return count > 0 ? Number((total / count).toFixed(1)) : 0;
};

/**
 * Calculate ranks from interview data
 */
export const calculateRanksFromInterviews = (
  interviews: InterviewData[]
): InterviewRank => {
  const ranks: InterviewRank = {
    html_css: 0,
    javascript: 0,
    react: 0,
    backend: 0,
  };

  interviews.forEach((interview: InterviewData) => {
    if (interview.overallScore) {
      const score = Math.round(interview.overallScore / 10);
      if (interview.moduleNumber === 0) ranks.html_css = score;
      else if (interview.moduleNumber === 1) ranks.javascript = score;
      else if (interview.moduleNumber === 2) ranks.react = score;
      else if (interview.moduleNumber === 3) ranks.backend = score;
    }
  });

  return ranks;
};

/**
 * Calculate ranks from student interview attempts
 */
export const calculateRanksFromAttempts = (
  attempts: InterviewAttempt[]
): InterviewRank => {
  const ranks: InterviewRank = {
    html_css: 0,
    javascript: 0,
    react: 0,
    backend: 0,
  };

  if (!Array.isArray(attempts)) {
    return ranks;
  }

  attempts.forEach((attempt: StudentAttempt) => {
    const moduleNumber =
      attempt?.moduleNumber ?? attempt?.interviewId?.moduleNumber;
    const overallScore = attempt?.overallScore;

    if (typeof moduleNumber !== "number" || typeof overallScore !== "number") {
      return;
    }

    const score = Math.round(overallScore / 10);

    if (moduleNumber === 0) ranks.html_css = score;
    else if (moduleNumber === 1) ranks.javascript = score;
    else if (moduleNumber === 2) ranks.react = score;
    else if (moduleNumber === 3) ranks.backend = score;
  });

  return ranks;
};

/**
 * Check if any rank exists
 */
export const hasAnyRank = (ranks: InterviewRank): boolean => {
  return Object.values(ranks).some((rank) => rank > 0);
};

/**
 * Filter interviews for a specific student
 */
export const filterInterviewsForStudent = (
  interviews: InterviewData[],
  studentId: string
): InterviewData[] => {
  const studentIdStr = studentId?.toString();
  return interviews.filter((interview: InterviewData) => {
    const interviewStudentId = interview.studentId?.toString();
    // Show if no studentId (batch-level) OR if it matches this student
    return !interviewStudentId || interviewStudentId === studentIdStr;
  });
};

/**
 * Get student-specific scored interviews
 */
export const getStudentScoredInterviews = (
  interviews: InterviewData[],
  studentId: string
): InterviewData[] => {
  const studentIdStr = studentId?.toString();
  return interviews.filter((interview: InterviewData) => {
    const interviewStudentId = interview.studentId?.toString();
    return interviewStudentId === studentIdStr && interview.overallScore;
  });
};

/**
 * Sort interviews by scheduled date
 */
export const sortInterviewsByDate = (
  interviews: InterviewData[],
  ascending: boolean = false
): InterviewData[] => {
  return [...interviews].sort((a, b) => {
    const dateA = new Date(a.scheduledDate).getTime();
    const dateB = new Date(b.scheduledDate).getTime();
    return ascending ? dateA - dateB : dateB - dateA;
  });
};
